import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunityMember } from '@/contexts/CommunityMemberContext';
import { useCommunity } from '@/contexts/CommunityContext';
import { useReviewReplies } from '@/hooks/useReviewReplies';
import ReviewList from '@/components/ReviewList';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from '@/hooks/use-toast';
import { CheckCircle2, XCircle, AlertTriangle, Star, MessageSquare } from 'lucide-react';
import { Review } from '@/hooks/useReviews';

interface ReviewWithReplies extends Review {
  businessName: string;
  review_replies?: Array<{
    id: string;
    content: string;
    status: 'Pending Validation' | 'Published' | 'Rejected';
    business_name: string;
    created_at: string;
  }>;
}

const ReviewManagement = () => {
  const { user } = useAuth();
  const { currentCommunity } = useCommunity();
  const { isAllowedTo } = useCommunityMember();
  const { validateReply } = useReviewReplies();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = React.useState("pending");
  
  const { data: canValidateReviews } = useQuery({
    queryKey: ['permissions', 'validate-reviews', user?.id],
    queryFn: async () => {
      if (!user) return false;
      return await isAllowedTo('view_all_reviews');
    },
    enabled: !!user
  });

  // Enhanced query that includes reviews with pending replies
  const { data: pendingReviews, isLoading: isPendingLoading, error: pendingError } = useQuery({
    queryKey: ['admin', 'reviews', 'pending', currentCommunity.id],
    queryFn: async () => {
      console.log('Fetching pending reviews...');
      
      // First get reviews that are pending validation
      const { data: pendingReviewsData, error: pendingReviewsError } = await supabase
        .from('reviews')
        .select(`
          id, 
          business_id, 
          user_id, 
          rating, 
          description, 
          status, 
          display_name, 
          created_at, 
          updated_at,
          businesses!inner(name, community_id),
          review_replies(
            id,
            content,
            status,
            business_name,
            created_at
          )
        `)
        .eq('businesses.community_id', currentCommunity.id)
        .eq('status', 'Pending Validation')
        .order('created_at', { ascending: false });

      if (pendingReviewsError) throw pendingReviewsError;

      // Second, get published reviews that have pending replies using a targeted approach
      const { data: publishedWithPendingRepliesData, error: publishedWithPendingRepliesError } = await supabase
        .from('reviews')
        .select(`
          id, 
          business_id, 
          user_id, 
          rating, 
          description, 
          status, 
          display_name, 
          created_at, 
          updated_at,
          businesses!inner(name, community_id),
          review_replies!inner(
            id,
            content,
            status,
            business_name,
            created_at
          )
        `)
        .eq('businesses.community_id', currentCommunity.id)
        .eq('status', 'Published')
        .eq('review_replies.status', 'Pending Validation')
        .order('created_at', { ascending: false });

      if (publishedWithPendingRepliesError) throw publishedWithPendingRepliesError;

      // Combine results
      const allPendingReviews = [...(pendingReviewsData || []), ...(publishedWithPendingRepliesData || [])];
      const uniqueReviews = allPendingReviews.filter((review, index, self) => 
        index === self.findIndex(r => r.id === review.id)
      );

      console.log('Pending reviews fetched:', uniqueReviews.length);

      // Normalize review_replies to always be an array
      return uniqueReviews.map(review => {
        let normalizedReplies = review.review_replies;
        
        // Ensure review_replies is always an array
        if (!Array.isArray(normalizedReplies)) {
          normalizedReplies = normalizedReplies ? [normalizedReplies] : [];
        }
        
        console.log(`Review ${review.id} has ${normalizedReplies.length} replies:`, normalizedReplies);
        
        return {
          ...review,
          businessName: review.businesses?.name || 'Unknown Business',
          review_replies: normalizedReplies
        };
      }) as ReviewWithReplies[];
    },
    enabled: !!user && !!canValidateReviews && !!currentCommunity.id
  });

  const { data: publishedReviews, isLoading: isPublishedLoading, error: publishedError } = useQuery({
    queryKey: ['admin', 'reviews', 'published', currentCommunity.id],
    queryFn: async () => {
      // Get published reviews but exclude those that have pending replies
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          id, 
          business_id, 
          user_id, 
          rating, 
          description, 
          status, 
          display_name, 
          created_at, 
          updated_at,
          businesses!inner(name, community_id),
          review_replies(
            id,
            content,
            status,
            business_name,
            created_at
          )
        `)
        .eq('businesses.community_id', currentCommunity.id)
        .eq('status', 'Published')
        .order('created_at', { ascending: false })
        .limit(50);
        
      if (error) throw error;
      
      // Filter out reviews that have pending replies - those should only appear in Pending tab
      const publishedWithoutPendingReplies = data.filter(review => {
        const replies = Array.isArray(review.review_replies) ? review.review_replies : [];
        const hasPendingReply = replies.some(reply => reply.status === 'Pending Validation');
        return !hasPendingReply;
      });
      
      return publishedWithoutPendingReplies.map(review => ({
        ...review,
        businessName: review.businesses?.name || 'Unknown Business'
      })) as ReviewWithReplies[];
    },
    enabled: !!user && !!canValidateReviews && selectedTab === "published" && !!currentCommunity.id
  });

  const { data: rejectedReviews, isLoading: isRejectedLoading, error: rejectedError } = useQuery({
    queryKey: ['admin', 'reviews', 'rejected', currentCommunity.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          id, 
          business_id, 
          user_id, 
          rating, 
          description, 
          status, 
          display_name, 
          created_at, 
          updated_at,
          businesses!inner(name, community_id),
          review_replies(
            id,
            content,
            status,
            business_name,
            created_at
          )
        `)
        .eq('businesses.community_id', currentCommunity.id)
        .eq('status', 'Rejected')
        .order('created_at', { ascending: false })
        .limit(50);
        
      if (error) throw error;
      
      return data.map(review => ({
        ...review,
        businessName: review.businesses?.name || 'Unknown Business'
      })) as ReviewWithReplies[];
    },
    enabled: !!user && !!canValidateReviews && selectedTab === "rejected" && !!currentCommunity.id
  });

  const validateReviewMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'Published' | 'Rejected' }) => {
      const { data, error } = await supabase
        .from('reviews')
        .update({ status })
        .eq('id', id)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      const action = variables.status === 'Published' ? 'approved' : 'rejected';
      toast({
        title: `Review ${action}`,
        description: `The review has been successfully ${action}.`,
        variant: "default",
      });
      
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to process the review",
        variant: "destructive",
      });
    }
  });

  const handleApprove = (reviewId: string) => {
    validateReviewMutation.mutate({ id: reviewId, status: 'Published' });
  };
  
  const handleReject = (reviewId: string) => {
    validateReviewMutation.mutate({ id: reviewId, status: 'Rejected' });
  };

  const handleApproveReply = (replyId: string) => {
    validateReply.mutate({ id: replyId, status: 'Published' });
  };

  const handleRejectReply = (replyId: string) => {
    validateReply.mutate({ id: replyId, status: 'Rejected' });
  };

  const renderRatingStars = (rating: number) => {
    return (
      <div className="flex items-center mt-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star 
            key={star}
            size={16}
            className={`${star <= rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}`}
          />
        ))}
        <span className="ml-1.5 text-sm font-medium">{rating.toFixed(1)}</span>
      </div>
    );
  };

  const renderReviewCard = (review: ReviewWithReplies) => {
    // Ensure review_replies is always treated as an array
    const replies = Array.isArray(review.review_replies) ? review.review_replies : [];
    const hasPendingReply = replies.some(reply => reply.status === 'Pending Validation');
    const isReviewPending = review.status === 'Pending Validation';

    console.log(`Rendering review ${review.id}:`, {
      hasPendingReply,
      isReviewPending,
      repliesCount: replies.length,
      replies: replies
    });

    return (
      <Card key={review.id} className="admin-card admin-card-action overflow-hidden ">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                <h3 className="font-semibold text-slate-800">{review.display_name}</h3>
                <div className="flex flex-wrap gap-2">
                  {hasPendingReply && (
                    <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 text-xs">
                      <MessageSquare className="w-3 h-3 mr-1" />
                      <span className="hidden sm:inline">Reply Pending</span>
                      <span className="sm:hidden">Reply</span>
                    </Badge>
                  )}
                  {isReviewPending && (
                    <Badge variant="outline" className="text-red-600 border-red-300 bg-red-50 text-xs">
                      <span className="hidden sm:inline">Review Pending</span>
                      <span className="sm:hidden">Pending</span>
                    </Badge>
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground/80">For: {review.businessName}</p>
            </div>
            <div className="flex justify-start sm:justify-end">
              {renderRatingStars(review.rating)}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-slate-700 mb-4 leading-relaxed">{review.description}</p>
          
          {/* Show pending replies */}
          {replies
             .filter(reply => reply.status === 'Pending Validation')
             .map(reply => (
            <div key={reply.id} className="bg-amber-50/80 dark:bg-amber-950/20 p-4 rounded-xl border border-amber-200/60 mb-4 transition-all hover:shadow-sm">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-amber-600" />
                  <span className="font-semibold text-amber-700">{reply.business_name}</span>
                  <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-100">
                    Reply Pending
                  </Badge>
                </div>
              </div>
              <p className="text-sm text-slate-700 mb-4 leading-relaxed">{reply.content}</p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400 rounded-lg w-full sm:w-auto"
                  onClick={() => handleRejectReply(reply.id)}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Reject Reply</span>
                  <span className="sm:hidden">Reject</span>
                </Button>
                <Button 
                  variant="default" 
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white rounded-lg w-full sm:w-auto"
                  onClick={() => handleApproveReply(reply.id)}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Approve Reply</span>
                  <span className="sm:hidden">Approve</span>
                </Button>
              </div>
            </div>
          ))}

          {/* Review action buttons - only show if review itself is pending */}
          {isReviewPending && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end pt-4 border-t border-gray-100">
              <Button 
                variant="outline" 
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400 rounded-lg w-full sm:w-auto order-2 sm:order-1"
                onClick={() => handleReject(review.id)}
              >
                <XCircle className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Reject Review</span>
                <span className="sm:hidden">Reject</span>
              </Button>
              <Button 
                variant="default" 
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white rounded-lg w-full sm:w-auto order-1 sm:order-2"
                onClick={() => handleApprove(review.id)}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Approve Review</span>
                <span className="sm:hidden">Approve</span>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const handleDeleteReview = (reviewId: string) => {
    console.log("Delete review functionality not implemented yet", reviewId);
  };

  // Calculate pending count including reviews with pending replies
  const pendingCount = pendingReviews?.length || 0;

  if (!canValidateReviews) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Card className="admin-card max-w-md p-8 text-center">
          <div className="mb-4 flex justify-center">
            <AlertTriangle className="h-12 w-12 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold mb-2 text-slate-800">Access Restricted</h2>
          <p className="text-muted-foreground/80">
            You don't have permission to view the review management page.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      <div className="relative">
        <h1 className="admin-section-title mb-2">Review Management</h1>
        <p className="text-muted-foreground/80 text-lg">
          Manage and moderate customer reviews and replies for all businesses.
        </p>
      </div>

      <Card className="admin-card admin-card-stats">
        <CardHeader className="pb-6">
          <CardTitle className="text-xl font-bold text-slate-800">Review Overview</CardTitle>
          <CardDescription className="text-muted-foreground/80 mt-2">
            Monitor and validate community reviews and business responses
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="w-full md:w-auto flex max-w-full overflow-x-auto">
              <TabsTrigger value="pending" className="flex-1">
                Pending 
                {pendingCount > 0 && (
                  <Badge variant="destructive" className="ml-2">{pendingCount}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="published" className="flex-1">Published</TabsTrigger>
              <TabsTrigger value="rejected" className="flex-1">Rejected</TabsTrigger>
            </TabsList>
            
            <TabsContent value="pending" className="mt-6">
              <div className="admin-card admin-card-action rounded-xl border border-gray-200/60 bg-white shadow-sm">
                <div className="p-6">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Reviews & Replies Pending Approval</h3>
                    <p className="text-muted-foreground/80">
                      Review and validate community submissions and business replies
                    </p>
                  </div>
                  {pendingReviews && pendingReviews.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-slate-800">All caught up!</h3>
                      <p className="text-muted-foreground/80">There are no reviews or replies waiting for approval.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {pendingReviews?.map(renderReviewCard)}
                    </div>
                  )}
                  
                  {isPendingLoading && (
                    <div className="flex justify-center py-12">
                      <div className="flex space-x-2">
                        <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
                        <div className="h-3 w-3 bg-gradient-to-r from-accent to-primary rounded-full animate-pulse animation-delay-200"></div>
                        <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse animation-delay-400"></div>
                      </div>
                    </div>
                  )}
                  
                  {pendingError && (
                    <div className="text-center py-8 text-destructive">
                      <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                      <p>Error loading pending reviews: {pendingError.message}</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="published" className="mt-6">
              <div className="admin-card admin-card-action rounded-xl border border-gray-200/60 bg-white shadow-sm">
                <div className="p-6">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Published Reviews</h3>
                    <p className="text-muted-foreground/80">
                      Reviews that have been approved and are visible to users
                    </p>
                  </div>
                  {publishedReviews && (
                    <ReviewList 
                      reviews={publishedReviews} 
                      isLoading={isPublishedLoading} 
                      error={publishedError as Error | null}
                    />
                  )}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="rejected" className="mt-6">
              <div className="admin-card admin-card-action rounded-xl border border-gray-200/60 bg-white shadow-sm">
                <div className="p-6">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Rejected Reviews</h3>
                    <p className="text-muted-foreground/80">
                      Reviews that have been rejected and are not visible to users
                    </p>
                  </div>
                  {rejectedReviews && (
                    <ReviewList 
                      reviews={rejectedReviews} 
                      isLoading={isRejectedLoading} 
                      error={rejectedError as Error | null}
                      onDeleteReview={handleDeleteReview}
                    />
                  )}
                  
                  {rejectedReviews && rejectedReviews.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-muted-foreground/80 mb-2">
                        To approve a rejected review, click the review and use the approve button.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReviewManagement;
